import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';

/*
 * RouteHeader — cabeçalho canônico de rota (renderizado dentro de .main .top).
 *
 * Fonte única de verdade pra:
 *   · separador de crumbs: /
 *   · tipografia: --shell-crumb-size (11px) + --shell-h1-size (19px, Manrope 800)
 *   · caixa: normal (não uppercase — nomes próprios de turma/escola/aluno)
 *   · gap crumb→title: 1-2px + line-height:1.1
 *
 * Antes deste componente, MainPage/SaveSchool/PanelHeader/PanelStudent
 * mantinham 3-4 implementações divergentes (separadores ·/›//, tamanhos e casos
 * diferentes). Toda rota consome este componente; nenhuma define fonte/separador
 * localmente.
 *
 * API:
 *   crumbs: [{ label: string|Node, to?: string }]
 *           - 1 nível: sem bold (o crumb é "onde estou", não "dentro de X")
 *           - 2+ níveis: intermediários weight 400 ink-400; último 700 ink-700
 *           Item com `to` vira Link clicável.
 *   title:  string | ReactNode
 *           string → render como <h1 class="rh-title">
 *           node   → passthrough (ex.: dropdown selector com chevron do Painel)
 *
 * Exemplos:
 *   Simples:      <RouteHeader crumbs={[{label:'Console'}]} title="Escolas" />
 *   Multi-nível:  <RouteHeader crumbs={[{label:'Painel'},{label:'Turmas',to:'/'}]} title="Bruno" />
 *   Com seletor:  <RouteHeader crumbs={[{label:'Painel'}]} title={<ClassSelectorButton/>} />
 */
const RouteHeader = ({ crumbs = [], title }) => {

	const lastIdx = crumbs.length - 1;
	// Bold no último só faz sentido quando existe hierarquia — 1 crumb sozinho
	// é "onde estou", não "dentro de X".
	const showParentBold = crumbs.length > 1;

	return (
		<div className="rh">
			{crumbs.length > 0 && (
				<div className="rh-crumbs">
					{crumbs.map((c, i) => {
						const isLast = i === lastIdx;
						const bold = isLast && showParentBold;
						/* 🔴 `state` no crumb `[PO DECIDIU 23/10/2026]`. O chevron e o
						   crumb levam ao MESMO destino, e só o chevron carregava o
						   contexto: vindo do "Em uso" de uma questão, a seta devolvia a
						   aba Classificação e o crumb ao lado devolvia a aba padrão. Dois
						   gatilhos para o mesmo lugar discordando é o defeito; quem os
						   iguala é esta linha. Ver o `PageHeader`, que injeta o
						   `backState` no crumb que aponta para o `backTo`. */
						const content = c.to
							? <Link to={c.to} state={c.state} className="rh-crumbs-link">{c.label}</Link>
							: c.label;
						/* `c.className` viaja pro ITEM e pro SEPARADOR que o segue
						   (2026-08-25). Esconder só o rótulo deixaria uma barra
						   órfã (" / ") na linha — o separador pertence ao crumb
						   que o precede, não ao próximo. Nasceu pra sumir com
						   `Painel` no mobile sem tirá-lo do desktop. */
						return (
							<Fragment key={i}>
								{bold
									? <b className={"rh-crumbs-parent" + (c.className ? " " + c.className : "")}>{content}</b>
									: <span className={c.className}>{content}</span>}
								{!isLast && <span className={"rh-crumbs-sep" + (c.className ? " " + c.className : "")}> / </span>}
							</Fragment>
						);
					})}
				</div>
			)}
			{title == null
				? null
				: typeof title === 'string' || typeof title === 'number'
					? <h1 className="rh-title">{title}</h1>
					: <div className="rh-title-slot">{title}</div>
			}
		</div>
	);
};

export default RouteHeader;
